import {InfoCircleOutlined} from '@ant-design/icons';
import {
    Button, Checkbox, Form, Input, InputNumber, Popconfirm, Tooltip,
} from 'antd';
import {useForm} from 'antd/es/form/Form';
import cn from 'classnames';
import React, {
    forwardRef,
    useContext, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import {v4 as uid} from 'uuid';

import {IconsMap} from 'components/dynamic-icon';
import {CustomSelect} from 'components/form/inputs/custom-select';
import {DateCell} from 'components/table/columns/date-cell/date-cell';
import {
    PredicateExpressionFormListContext,
    IPredicateExpressionGroupRef,
    IPredicateExpressionOperandRef,
    TPredicateExpressionNode,
    EFilterValueType,
    getFilterValueTypeByColumnType
} from 'features/predicate-expression-form-list';
import {DATE, DATE_DMY} from 'shared/constants/date-format';
import {useAppSelector} from 'store/config/hooks';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';
import {
    loadTableReportColumnsLookup,
    IReportTableColumn,
} from 'store/slices/report-configuration-slice/dd-lookups';
import {
    selectTableReportConfigurationFilterConditions,
    selectTableReportConfigurationFiltersByFieldType,
} from 'store/slices/report-configuration-slice/dd-lookups/dd-lookups-selectors';
import {loadTableReportConfigurationFilterConditionsLookup} from 'store/slices/report-configuration-slice/dd-lookups/dd-lookups-thunks';
import {
    selectDdParametersAsEntries,
    selectReportConfigurationTableAttributes,
} from 'store/slices/report-configuration-slice/report-configuration-slice';

import {
    findReportColumnByFieldName,
    findTableReportConditionByOperatorCode,
} from '../../report-dd-filters/dd-filter-expression-creator/dd-filter-expression-creator-utils';
import {DdRestrictionExpressionGroup} from './dd-restriction-expression-group';

interface IDdRestrictionExpressionOperandProps {
    operand?: TPredicateExpressionNode;
    isEditing: boolean;
    remove: () => void;
    depth: number;
    itemIndex: string;
}

export const DdRestrictionExpressionOperand = forwardRef<
    IPredicateExpressionOperandRef,
    IDdRestrictionExpressionOperandProps
>(({
    operand,
    isEditing,
    remove,
    depth,
    itemIndex,
}: IDdRestrictionExpressionOperandProps, ref) => {
    const [form] = useForm();

    const expressionRef = useRef<IPredicateExpressionGroupRef>(null);

    const [isParametrizedInputVisible, setIsParametrizedFieldVisible] = useState(
        Array.isArray(operand) ? false : !!operand?.parametrized,
    );

    const ddParametersEntries = useAppSelector(selectDdParametersAsEntries);

    const [selectedReportColumn, setSelectedReportColumn] = useState<IReportTableColumn>();
    const [isComparisonValueAllowedEmpty, setIsComparisonValueAllowedEmpty] = useState(false);

    const comparisonValueFieldType = getFilterValueTypeByColumnType(selectedReportColumn?.type);

    const {
        templateCode,
    } = useContext(PredicateExpressionFormListContext);

    const isLoadingColumns = useAppSelector(s => selectIsThunkPending(s, loadTableReportColumnsLookup.typePrefix));
    const reportColumns = useAppSelector(s => (!templateCode
        ? undefined : selectReportConfigurationTableAttributes(s)));

    const tableReportFilterConditions = useAppSelector(selectTableReportConfigurationFilterConditions);
    const tableReportFilterConditionsByFieldType = useAppSelector(
        s => selectTableReportConfigurationFiltersByFieldType(s, {
            fieldType: comparisonValueFieldType,
        }),
    );

    const isLoadingTableReportFilterConditions = useAppSelector(s => selectIsThunkPending(
        s, loadTableReportConfigurationFilterConditionsLookup.typePrefix,
    ));

    const getValues = async (skipValidation = false) => {
        if (!Array.isArray(operand)) {
            const isValid = await (async () => {
                let result = true;
                try {
                    await form.validateFields();
                } catch (e) {
                    if (e?.errorFields?.length) result = false;
                }
                return result;
            })();

            if (!isValid && !skipValidation) return Promise.reject(new Error('Form validation error'));

            const values: TPredicateExpressionNode = {
                fieldName: form.getFieldValue('fieldName'),
                comparisonValue: form.getFieldValue('comparisonValue'),
                parametrized: form.getFieldValue('parametrized'),
                enabled: form.getFieldValue('enabled'),
                operatorCode: form.getFieldValue('operatorCode'),
            };

            return values;
        }
        return expressionRef.current?.getValues?.(skipValidation);
    };

    useImperativeHandle(ref, () => ({getValues}));

    useEffect(() => {
        form.resetFields();
        if (!Array.isArray(operand)) {
            setIsParametrizedFieldVisible(!!operand?.parametrized);
            setSelectedReportColumn(findReportColumnByFieldName({
                reportColumns,
                fieldName: operand?.fieldName,
            }));
            setIsComparisonValueAllowedEmpty(
                operand?.operatorCode === 'EMPTY' || operand?.operatorCode === 'NOT_EMPTY',
            );
        }
    }, [operand]);

    const groupIdentificator = useMemo(() => {
        if (!Array.isArray(operand)) return undefined;
        let ident = operand[0];
        if (!ident) return {connectBy: 'OR' as 'OR' | 'AND', enabled: false, uid: uid()};
        if (!ident.uid) ident = {...ident, uid: uid()};
        if (!ident.enabled) ident = {...ident, enabled: false};
        return ident;
    }, [operand]);

    if (!Array.isArray(operand)) {
        return (
            <Form
                layout="vertical"
                form={form}
                onValuesChange={changed => {
                    if (changed.parametrized !== undefined) {
                        setIsParametrizedFieldVisible(changed.parametrized);
                        form.setFieldsValue({comparisonValue: undefined});
                    }
                    if (changed.operatorCode !== undefined) {
                        const nextReportCondition = findTableReportConditionByOperatorCode({
                            filterLookups: tableReportFilterConditions,
                            operatorCode: changed?.operatorCode,
                        });

                        const {lookupCode: nextConditionLookupCode} = nextReportCondition ?? {};
                        if (nextConditionLookupCode === 'EMPTY' || nextConditionLookupCode === 'NOT_EMPTY') {
                            setIsComparisonValueAllowedEmpty(true);
                            form.setFieldsValue({comparisonValue: undefined});
                        } else setIsComparisonValueAllowedEmpty(false);
                    }
                    if (changed.fieldName !== undefined) {
                        const nextFieldName = changed.fieldName;
                        const nextSelectedReportColumn = findReportColumnByFieldName({
                            reportColumns,
                            fieldName: nextFieldName,
                        });

                        setSelectedReportColumn(nextSelectedReportColumn);
                        form.setFieldsValue({comparisonValue: undefined, operatorCode: undefined});
                    }
                }}
            >
                <div
                    className={cn('dd-filter-expression-operand__item')}
                >
                    <div className={cn('dd-filter-expression-operand__item__body')}>
                        <div className={cn('dd-filter-expression-operand__item__body__title', 'fw-500')}>
                            Правило №{itemIndex}
                        </div>
                        <div className={cn('dd-filter-expression-operand__item__body__form')}>
                            <Form.Item
                                className={cn('dd-filter-expression-operand__item__body__form-item__select')}
                                label="Поле документа"
                                required
                                name="fieldName"
                                rules={[{required: true, message: 'Пожалуйста, выберите поле'}]}
                                initialValue={operand?.fieldName}
                            >
                                <CustomSelect
                                    style={{minWidth: 236}}
                                    entries={(reportColumns ?? []).map((col => ({
                                        label: col.name,
                                        value: col.name,
                                    })))}
                                    settings={{
                                        isLoading: isLoadingColumns,
                                        isDisabled: !isEditing || isLoadingColumns,
                                        placeholder: 'Выберите поле',
                                        showSearch: true,
                                    }}
                                    fixedDropdown={false}
                                />
                            </Form.Item>

                            <Form.Item
                                className={cn('dd-filter-expression-operand__item__body__form-item__select')}
                                label="Условие"
                                required
                                name="operatorCode"
                                rules={[{required: true, message: 'Пожалуйста, выберите условие'}]}
                                initialValue={operand?.operatorCode}
                            >
                                <CustomSelect
                                    entries={(tableReportFilterConditionsByFieldType ?? []).map((filterLookup => ({
                                        label: filterLookup.meaning,
                                        value: filterLookup.lookupCode,
                                    })))}
                                    settings={{
                                        isDisabled: !isEditing || isLoadingTableReportFilterConditions,
                                        placeholder: 'Выберите условие',
                                    }}
                                    fixedDropdown={false}
                                />
                            </Form.Item>

                            {!isParametrizedInputVisible && (
                                <Form.Item
                                    className={cn('dd-filter-expression-operand__item__body__form-item__input')}
                                    label="Значение"
                                    required={!isComparisonValueAllowedEmpty}
                                    name="comparisonValue"
                                    rules={[{
                                        required: !isComparisonValueAllowedEmpty,
                                        message: 'Пожалуйста, введите значение',
                                    }]}
                                    initialValue={operand?.comparisonValue}
                                >
                                    {(() => {
                                        const isDisabled = !isEditing || isComparisonValueAllowedEmpty;

                                        if (comparisonValueFieldType === EFilterValueType.number) {
                                            return (
                                                <InputNumber
                                                    className="w-100"
                                                    disabled={isDisabled}
                                                    placeholder="Введите значение"
                                                    decimalSeparator=","
                                                />
                                            );
                                        }
                                        if (comparisonValueFieldType === EFilterValueType.date) {
                                            return (
                                                <DateCell
                                                    format={DATE_DMY}
                                                    requestFormat={DATE}
                                                    disabled={isDisabled}
                                                />
                                            );
                                        }
                                        return (
                                            <Input
                                                disabled={isDisabled}
                                                placeholder="Введите значение"
                                            />
                                        );
                                    })()}
                                </Form.Item>
                            )}

                            {isParametrizedInputVisible && (
                                <Form.Item
                                    className={cn(
                                        'dd-filter-expression-operand__item__body__form-item__input',
                                        'dd-filter-expression-operand__item__body__form-item__select',
                                    )}
                                    label={(
                                        <div className="d-flex gap-0-75 align-items-center">
                                            Переменная
                                            <Tooltip
                                                title={'Для вывода данных в поле необходимо'
                                                    + ' настроить правила передачи динамических значений'}
                                            >
                                                <InfoCircleOutlined
                                                    className={
                                                        'dd-filter-expression-operand'
                                                        + '__item__body__form-item__tooltip-icon'
                                                    }
                                                />
                                            </Tooltip>
                                        </div>
                                    )}
                                    required
                                    name="comparisonValue"
                                    rules={[{required: true, message: 'Пожалуйста, выберите переменную'}]}
                                    initialValue={operand?.comparisonValue}
                                >
                                    <CustomSelect
                                        entries={ddParametersEntries}
                                        settings={{
                                            placeholder: 'Выберите переменную',
                                            isDisabled: !isEditing || isComparisonValueAllowedEmpty,
                                        }}
                                        fixedDropdown={false}
                                    />
                                </Form.Item>
                            )}

                            <Form.Item
                                className={cn('ml-1', 'dd-filter-expression-operand__item__body__form-item__checkbox')}
                                label={<>&nbsp;</>}
                                name="enabled"
                                initialValue={operand?.enabled}
                                valuePropName="checked"
                            >
                                <Checkbox disabled={!isEditing}>Активно</Checkbox>
                            </Form.Item>

                            <Popconfirm
                                title={(
                                    <span>Вы действительно хотите удалить правило?</span>
                                )}
                                placement="bottomLeft"
                                okText="Удалить"
                                disabled={!isEditing}
                                onConfirm={() => {
                                    remove?.();
                                }}
                            >
                                <div>
                                    <Button
                                        className={cn(
                                            'dd-filter-expression-operand__item__body__form-button',
                                            'btn-only-icon',
                                            'button-critic',
                                        )}
                                        type="default"
                                        disabled={!isEditing}
                                    >
                                        <IconsMap.TrashXOutlined />
                                    </Button>
                                </div>
                            </Popconfirm>
                        </div>
                    </div>
                </div>
            </Form>
        );
    }

    return (
        <div className={cn('dd-filter-expression-operand__group')}>
            <DdRestrictionExpressionGroup
                isEditing={isEditing}
                initialExpression={[
                    groupIdentificator
                        ?? {connectBy: 'OR', uid: uid()}, ...operand.slice(1) as TPredicateExpressionNode[],
                ]}
                ref={expressionRef}
                isChild
                remove={() => {
                    remove?.();
                }}
                depth={depth + 1}
                itemIndex={itemIndex}
            />
        </div>
    );
});
